'use client';

import { RefreshCw } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { SITE } from '@/app/constants';
import { useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { reportEvent } from '@/app/utils';
import { useLocale, usePathname, useRouter } from '@/i18n/routing';
import { type EarnActivity, listFeaturedActivities } from '../_apis';
import { useEarnStatus } from '../_hooks/use-earn-status';
import { EARN_LEARN_MORE_URL } from '../_utils';
import { LandingActivityCard } from './_components';
import { Feat1 } from './_icons/Feat1';
import { Feat2 } from './_icons/Feat2';
import { Feat3 } from './_icons/Feat3';
import { Feat4 } from './_icons/Feat4';

type LoadState = 'idle' | 'loading' | 'success' | 'error';
const FEATURE_KEYS = ['featureExclusive', 'featureBrand', 'featureMonetization', 'featureWithdrawals'] as const;
const FEATURE_SUBTITLE_KEYS = [
  'featureExclusiveSubtitle',
  'featureBrandSubtitle',
  'featureMonetizationSubtitle',
  'featureWithdrawalsSubtitle',
] as const;
const BENEFIT_KEYS = [
  { title: 'benefit1Title', description: 'benefit1Desc' },
  { title: 'benefit2Title', description: 'benefit2Desc' },
] as const;
const FEATURED_ACTIVITY_LIMIT = 10;
const EARN_MEDIA_BASE_URL =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset'
    : 'https://image01.cf.vidu.studio/vidu/media-asset';
const HERO_IMAGES = ['1-5f98422b.webp', '2-94efbcc1.webp'] as const;
const FEATURE_IMAGES = [
  'feat1-572cd947.webp',
  'feat2-e1f1779d.webp',
  'feat3-850dd0a3.webp',
  'feat4-adc3f50d.webp',
] as const;
const FEATURE_ICONS = [Feat1, Feat2, Feat3, Feat4] as const;

export default function EarnLandingPage() {
  const t = useTranslations('earn.landing');
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();
  const userInfo = useStore((state) => state.userInfo);
  const { open: openLoginDialog } = useGlobalModal('loginDialog');
  const { refreshStatus, status } = useEarnStatus();
  const [activities, setActivities] = useState<EarnActivity[]>([]);
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const isApprovedCreator = status === 'approved';

  const loadActivities = async () => {
    setLoadState('loading');
    try {
      const nextActivities = await listFeaturedActivities(locale);
      setActivities(nextActivities.slice(0, FEATURED_ACTIVITY_LIMIT));
      setLoadState('success');
    } catch {
      setLoadState('error');
    }
  };

  useEffect(() => {
    loadActivities();
  }, [locale]);

  useEffect(() => {
    reportEvent('earn_landing_expose');
  }, []);

  useEffect(() => {
    if (!userInfo || status) return;

    void refreshStatus().catch(() => undefined);
  }, [refreshStatus, status, userInfo]);

  const handleApply = () => {
    reportEvent(isApprovedCreator ? 'earn_act_center_click' : 'earn_landing_signup_click');

    if (!userInfo) {
      openLoginDialog({ redirectUrl: '/earn/apply' });
      reportEvent('login_pop_exposure', {
        refer_element: 'earn-apply',
        refer_page: pathname,
      });
      return;
    }

    if (isApprovedCreator) {
      router.push('/earn/activities');
      return;
    }

    router.push('/earn/apply');
  };

  return (
    <>
      <section className="relative px-12 pt-[154px] pb-[124px] sm:px-4 sm:pt-28 sm:pb-16">
        <div className="relative mx-auto flex w-full max-w-[1440px] flex-col items-center text-center">
          <h1 className="max-w-[1080px] font-semibold text-[76px] text-white leading-[1.05] tracking-normal sm:text-4xl">
            {t.rich('heroTitle', {
              br: () => <br />,
              gradient: (chunks) => (
                <span className="inline-block whitespace-nowrap bg-gradient-to-r from-[#5372ff] via-[#9376ff] to-[#f1a5b7] bg-clip-text text-transparent">
                  {chunks}
                </span>
              ),
            })}
          </h1>

          <div className="mt-9 flex flex-wrap items-center justify-center gap-5 sm:mt-8 sm:gap-3">
            <button
              type="button"
              onClick={handleApply}
              className="inline-flex h-[58px] min-w-[218px] items-center justify-center rounded-full bg-[#305AE0] px-8 font-semibold text-lg text-system-white transition hover:bg-[#305AE0]/80 sm:h-12 sm:min-w-[154px] sm:px-5 sm:text-base"
            >
              {isApprovedCreator ? t('viewActivities') : t('signUp')}
            </button>
            <a
              href={EARN_LEARN_MORE_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-[58px] min-w-[202px] items-center justify-center rounded-full border border-system-white12 bg-white/[0.08] px-8 font-semibold text-lg text-white transition hover:bg-white/[0.14] sm:h-12 sm:min-w-[150px] sm:px-5 sm:text-base"
            >
              {t('learnMore')}
            </a>
          </div>

          <div className="mt-[76px] grid w-full grid-cols-2 gap-6 sm:mt-10 sm:grid-cols-1 sm:gap-7">
            {HERO_IMAGES.map((image, index) => (
              <article key={image} className="min-w-0 text-left">
                <div className="relative aspect-[1400/710]">
                  <img
                    src={`${EARN_MEDIA_BASE_URL}/${image}`}
                    alt=""
                    aria-hidden="true"
                    loading={index === 0 ? 'eager' : 'lazy'}
                    className="size-full object-cover"
                  />
                </div>
                <div className="mt-7 flex flex-col items-center px-1 text-center sm:mt-4">
                  <h2 className="font-semibold text-[28px] text-white leading-9 sm:text-xl sm:leading-7">
                    {t(BENEFIT_KEYS[index].title)}
                  </h2>
                  <p className="mt-3 max-w-[620px] text-lg text-system-white48 leading-7 sm:text-base sm:leading-6">
                    {t(BENEFIT_KEYS[index].description)}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="relative px-12 pb-[132px] sm:px-4 sm:pb-16">
        <div className="mx-auto flex w-full max-w-[1440px] flex-col items-center text-center">
          <h2 className="font-semibold text-[40px] text-white leading-tight tracking-normal sm:text-3xl">
            {t('whatIsTitle')}
          </h2>
          <div className="mt-12 grid w-full grid-cols-4 gap-8 sm:mt-8 sm:grid-cols-1 sm:gap-4 lg:gap-5">
            {FEATURE_KEYS.map((feature, index) => (
              <article key={feature} className="min-w-0 text-left">
                <div className="relative aspect-[336/474] overflow-hidden rounded-[24px] bg-white/[0.04] sm:rounded-[18px] md:aspect-auto md:h-[320px]">
                  <img
                    src={`${EARN_MEDIA_BASE_URL}/${FEATURE_IMAGES[index]}`}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    className="size-full object-cover"
                  />
                  <div className="absolute inset-x-0 top-0 flex min-h-[190px] flex-col items-start justify-start bg-gradient-to-b from-black/78 via-black/34 to-transparent px-10 pt-12 text-white sm:min-h-[210px] md:p-5 lg:p-8">
                    {(() => {
                      const Icon = FEATURE_ICONS[index];

                      return (
                        <Icon
                          className="mb-4 size-24 text-system-white md:mb-2 md:size-16 lg:mb-3 lg:size-20"
                          aria-hidden="true"
                        />
                      );
                    })()}
                    <h3 className="font-semibold text-3xl md:text-xl lg:text-2xl">{t(feature)}</h3>
                    <p className="mt-4 text-system-white text-xl sm:text-base md:mt-2 md:text-sm lg:mt-3 lg:text-base">
                      {t(FEATURE_SUBTITLE_KEYS[index])}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="relative px-12 pb-28 sm:px-4" id="earn-activities">
        <div className="mx-auto w-full max-w-[1440px]">
          <div className="mb-12 text-center">
            <h2 className="mt-3 font-semibold text-[40px] text-white tracking-normal sm:text-3xl">
              {t('featuredTitle')}
            </h2>
          </div>

          {loadState === 'loading' && (
            <div className="grid grid-cols-3 gap-5 sm:grid-cols-1 lg:grid-cols-2">
              {['activity-skeleton-primary', 'activity-skeleton-secondary', 'activity-skeleton-tertiary'].map((key) => (
                <div
                  key={key}
                  className="h-[360px] animate-pulse rounded-8 border border-system-white10 bg-white/[0.055]"
                />
              ))}
            </div>
          )}

          {loadState === 'error' && (
            <div className="flex min-h-[260px] flex-col items-center justify-center rounded-8 border border-system-white10 bg-white/[0.04] text-center">
              <p className="font-medium text-white">{t('loadError')}</p>
              <p className="mt-2 text-sm text-system-white48">{t('loadErrorHint')}</p>
              <button
                type="button"
                onClick={loadActivities}
                className="mt-6 inline-flex h-10 items-center gap-2 rounded-8 border border-system-white12 px-4 text-sm text-white transition hover:bg-white/10"
              >
                <RefreshCw className="size-4" />
                {t('retry')}
              </button>
            </div>
          )}

          {loadState === 'success' && activities.length === 0 && (
            <div className="flex min-h-[260px] items-center justify-center rounded-8 border border-system-white10 bg-white/[0.04] text-center">
              <div>
                <p className="font-medium text-white">{t('noActivities')}</p>
                <p className="mt-2 text-sm text-system-white48">{t('noActivitiesHint')}</p>
              </div>
            </div>
          )}

          {loadState === 'success' && activities.length > 0 && (
            <div className="grid grid-cols-3 gap-5 sm:grid-cols-1 lg:grid-cols-2">
              {activities.map((activity, index) => (
                <LandingActivityCard key={activity.id ?? `featured-activity-${index}`} activity={activity} />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
